/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { ISpinnerStyleProps, ISpinnerStyles, Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type MigrationModalFooterActions = {
    onSubmit?: () => void;
    onContinue?: () => void;
    onPrevious?: () => void;
};

export type MigrationModalFooterProps = MigrationModalFooterActions & {
    /**
     * Indicates whether the footer back button should be disabled.
     */
    backDisabled?: boolean;

    /**
     * Indicates whether the footer next button should be disabled.
     */
    nextDisabled?: boolean;

    /**
     * Indicates whether the footer finish button should be disabled.
     */
    finishDisabled?: boolean;

    /**
     * Indicates whether the current step is one of the region migration
     * steps.
     */
    isRegionMigrationStep?: boolean;

    /**
     * Indicates whether the footer in a loading state.
     */
    isLoading?: boolean;
};

const messages = defineMessages({
    next: {
        id: 'multiRegionMigrationModal.multiRegionMigrationModalFooter.next',
        defaultMessage: 'Next'
    },
    back: {
        id: 'multiRegionMigrationModal.multiRegionMigrationModalFooter.back',
        defaultMessage: 'Back'
    },
    migrate: {
        id: 'multiRegionMigrationModal.multiRegionMigrationModalFooter.done',
        defaultMessage: 'Migrate and Next'
    },
    finish: {
        id: 'multiRegionMigrationModal.multiRegionMigrationModalFooter.finish',
        defaultMessage: 'Finish'
    }
});

const mainButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { marginRight: '8px' } });
const spinnerStyles = classNamesFunction<ISpinnerStyleProps, ISpinnerStyles>()({ circle: { borderWidth: '3px' } });

/**
 * Displays the modal footer with suitable text related to active step.
 */
export const MigrationModalFooter = (props: MigrationModalFooterProps) => {
    const {
        onSubmit,
        onContinue,
        onPrevious,
        backDisabled,
        nextDisabled,
        isRegionMigrationStep = false,
        finishDisabled = true,
        isLoading = false
    } = props;
    const { localize } = useLocalization();

    return (
        <Stack horizontal horizontalAlign="space-between">
            <Stack horizontal horizontalAlign="start" tokens={{ childrenGap: 5 }}>
                <PrimaryButton
                    onClick={onPrevious}
                    styles={mainButtonStyles}
                    text={localize(messages.back)}
                    data-automation-id="backButton"
                    disabled={backDisabled || isLoading}
                />

                <PrimaryButton
                    onClick={onContinue}
                    styles={mainButtonStyles}
                    data-automation-id="nextButton"
                    disabled={nextDisabled || isLoading}
                    text={localize(isRegionMigrationStep ? messages.migrate : messages.next)}
                />

                {isLoading && <Spinner size={SpinnerSize.small} styles={spinnerStyles} />}
            </Stack>

            <DefaultButton
                onClick={onSubmit}
                disabled={finishDisabled}
                styles={mainButtonStyles}
                text={localize(messages.finish)}
                data-automation-id="finishButton"
            />
        </Stack>
    );
};
